import { CustomReportCategory } from '../../../../../../redux/context/customReports/typesCategory'
import { CustomReportVariable } from '../../../../../../redux/context/customReports/typesVariable'
import { DisplayScale, resolveAutoScale } from '../../../../../../shared/utils/numberScale'
import { VariableType } from '../../../../../../components/Table/types'
import { TransposedRowData as GridRowData } from './transposedRowData'
import { buildNumberFormat } from '../../../../../../shared/utils/excelNumberFormat'
import {
  CellExportMetadata,
  CellExportMetadataProvider
} from '../../../../../../shared/utils/excelJsExport/excelExportTypes'
import { getCellBackgroundColor, renderVariableTitle } from './renderingUtils'
import { resolveFormat } from './valueFormatting'

interface BuildMetadataArgs {
  value: number | string | undefined
  variable: CustomReportVariable | undefined
  category: CustomReportCategory | undefined
  bgColor?: string
}

const buildMetadata = ({ value, variable, category, bgColor }: BuildMetadataArgs): CellExportMetadata => {
  const isNumeric = typeof value === 'number' && !Number.isNaN(value)
  const { numberFormat, variableType } = resolveFormat(variable, category)
  const isPercentage = variableType === VariableType.percentage

  // `auto` is a display-time concept; Excel format strings are per-cell so
  // resolve it to a concrete scale using the cell's own magnitude.
  const resolvedFormat =
    numberFormat.displayScale === DisplayScale.auto && !isPercentage && isNumeric
      ? { ...numberFormat, displayScale: resolveAutoScale(value as number) }
      : numberFormat

  // Value is kept raw — scaling is done by the Excel number format string
  // (`#,` / `#,,`) so the underlying number stays calculable.
  return {
    value,
    numberFormat: isNumeric ? buildNumberFormat(resolvedFormat, isPercentage) : undefined,
    bold: !!variable?.style?.fontWeight,
    italic: variable?.style?.fontStyle === 'italic',
    bgColor,
    align: 'right'
  }
}

const getFieldValue = (obj: unknown, field: string): string | undefined => {
  if (!obj || typeof obj !== 'object') return undefined
  const parts = field.split('.')
  let current: any = obj
  for (const part of parts) {
    if (current == null) return undefined
    current = current[part]
  }
  return current == null ? undefined : String(current)
}

/**
 * Cell metadata provider for `CustomReportAgGrid` (variables as rows,
 * categories as columns). Column context carries the `CustomReportCategory`.
 */
export const variableRowCellMetadataProvider: CellExportMetadataProvider = (node, column) => {
  const variable = node.data as CustomReportVariable | undefined
  const colDef = column.getColDef()
  const category = (colDef.context as { category?: CustomReportCategory } | undefined)?.category

  // Auto-group / non-category columns (e.g. the variable name column) have no
  // category context — render the variable title as plain text.
  if (!category) {
    return {
      value: variable ? renderVariableTitle(variable, []) : undefined,
      bold: !!variable?.style?.fontWeight,
      italic: variable?.style?.fontStyle === 'italic',
      align: 'left',
      bgColor: getCellBackgroundColor(undefined, variable)
    }
  }

  const rawValue = variable?.balances?.find(b => b.groupId === category?.id)?.value
  const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue

  return buildMetadata({
    value,
    variable,
    category,
    bgColor: getCellBackgroundColor(category, variable, typeof value === 'number' ? value : undefined)
  })
}

/**
 * Cell metadata provider for `CustomReportAgGridTransposed` (leaf categories as
 * rows, variables as columns). Column context carries the
 * `CustomReportVariable`.
 */
export const categoryRowCellMetadataProvider: CellExportMetadataProvider = (node, column) => {
  const rowData = node.data as GridRowData | undefined
  const colDef = column.getColDef()
  const ctx = colDef.context as { variable?: CustomReportVariable } | undefined
  const variable = ctx?.variable
  const leafCategory = rowData?.leafCategory

  // Non-variable columns (category name, company, dimension) — plain text
  if (!variable) {
    const fieldValue = colDef.field ? getFieldValue(rowData, colDef.field) : undefined
    return {
      value: fieldValue,
      align: 'left',
      bgColor: getCellBackgroundColor(leafCategory)
    }
  }

  const rawValue = variable.balances?.find(b => b.groupId === rowData?.categoryId)?.value
  const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue

  return buildMetadata({
    value,
    variable,
    category: leafCategory,
    bgColor: getCellBackgroundColor(leafCategory, variable, typeof value === 'number' ? value : undefined)
  })
}
